/**
 * Caliper crosshair endpoints for linear measurements (AP-810).
 *
 * Drag/select is unaffected: Cornerstone hit-tests the pointer against
 * `data.handles.points` on a proximity constant, never against what is drawn.
 */

import { drawing as csDrawing } from '@cornerstonejs/tools';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import { ANNOTATION_STYLE } from '@/theme/constants';

/** LengthTool's own handle group — see its renderAnnotation. */
const CORNERSTONE_HANDLE_GROUP_UID = '0';

export interface CrosshairMetrics {
  /** Half-length of each arm, canvas px. */
  arm: number;
  width: number;
}

/** Selected endpoints read longer and heavier; hue carries the source. */
export function crosshairMetrics(isSelected: boolean): CrosshairMetrics {
  return isSelected
    ? {
        arm: ANNOTATION_STYLE.crosshairArmActive,
        width: ANNOTATION_STYLE.crosshairWidthActive,
      }
    : {
        arm: ANNOTATION_STYLE.crosshairArmInactive,
        width: ANNOTATION_STYLE.crosshairWidthInactive,
      };
}

/** Draws one crosshair per canvas point, centred on the point. */
export function drawEndpointCrosshairs(
  svgDrawingHelper: Parameters<typeof csDrawing.drawLine>[0],
  annotationUID: string,
  canvasPoints: CornerstoneTypes.Point2[],
  { color, arm, width }: CrosshairMetrics & { color?: string }
): void {
  if (!canvasPoints.length) return;

  // LengthTool renders its own handle at this group on hover and drag.
  // Claim the node transparently so only the crosshair shows.
  csDrawing.drawHandles(
    svgDrawingHelper,
    annotationUID,
    CORNERSTONE_HANDLE_GROUP_UID,
    canvasPoints,
    { opacity: 0, handleRadius: 0 }
  );

  canvasPoints.forEach((point, i) => {
    const [x, y] = point;
    // Per-endpoint node UIDs so the helper diffs rather than repaints.
    // Never dashed — a dash across a 4-5px arm erases it.
    csDrawing.drawLine(
      svgDrawingHelper,
      annotationUID,
      `${annotationUID}-xh-${i}-h`,
      [x - arm, y],
      [x + arm, y],
      { color, width }
    );
    csDrawing.drawLine(
      svgDrawingHelper,
      annotationUID,
      `${annotationUID}-xh-${i}-v`,
      [x, y - arm],
      [x, y + arm],
      { color, width }
    );
  });
}
